import { type ReactNode, useId, useState } from "react";
import type {
  QuestionStoreResponse,
  ServedQuestion,
  ServedQueue,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { DisclosureBar } from "@/components/ui/Disclosure";
import { EmptyState } from "@/components/ui/EmptyState";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { useQuestionWrites } from "@/hooks/use-questions";
import { plural } from "@/lib/plural";
import { refusalDetail } from "@/services/api-client";
import { QuestionWords } from "./QuestionWords";
import { NOT_SAID, ReplyControls } from "./ReplyControls";

/** Why the count of questions left can move, as v0.29's pill says it. */
const COUNT_CHANGES =
  "This number can change as you answer: some answers make later questions unnecessary, others lead to follow-ups.";

/** What the answers help Praxis understand, at each trigger. */
export type QuestionTopic = "invention" | "prior art" | "claim strategy";

type Writes = ReturnType<typeof useQuestionWrites>;

/**
 * Whether the thread can move past the question: a reply or a skip at a
 * commit; on the review, only a reply, since a skipped question is what it is
 * there to answer.
 */
function settled(question: ServedQuestion, panel: boolean): boolean {
  return Boolean(question.draft) || (!panel && question.status !== "open");
}

/** The folded note under a question: why it is asked, and what it affects. */
export function WhyNote({ question }: { question: ServedQuestion }) {
  return (
    <div className="mt-2">
      <DisclosureBar label="Why am I being asked this?">
        <p>{question.reason}</p>
        {question.affects_said !== null && <p>{question.affects_said}</p>}
      </DisclosureBar>
    </div>
  );
}

/**
 * The card's head: who asks, how many questions, and how many are left, the
 * count at the row's end as v0.29 draws it.
 */
function CardHeading({
  id,
  asked,
  left,
}: {
  id: string;
  asked: number;
  left: number;
}) {
  return (
    <>
      <MicroLabel size="sm" className="text-primary">
        Praxis
      </MicroLabel>
      <div className="mt-1 flex items-center gap-2.5">
        <h2 id={id} className="text-[17px] font-bold tracking-tight">
          {asked > 1 ? "A few questions..." : "Just one question..."}
        </h2>
        <StatusPill
          tone="raised"
          size="chip"
          plain
          className="ml-auto"
          title={COUNT_CHANGES}
        >
          {left > 0 ? `${plural(left, "question")} left` : "All answered"}
        </StatusPill>
      </div>
    </>
  );
}

/** Praxis asking: its name, then the question in a bubble with the why note. */
function Asked({
  taskId,
  question,
  children,
}: {
  taskId: string;
  question: ServedQuestion;
  children?: ReactNode;
}) {
  return (
    <div className="flex max-w-[92%] items-start gap-2.5">
      <StatusPill tone="brand" plain size="sm" className="mt-1">
        Praxis
      </StatusPill>
      <Panel pad="snug" className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold">
          <QuestionWords taskId={taskId} text={question.question} />
        </p>
        <WhyNote question={question} />
        {children}
      </Panel>
    </div>
  );
}

/**
 * The attorney's reply as it stands, or that they put it off, with the way to
 * change it; a question Praxis closed reads as no longer needed, with nothing to change.
 */
function Replied({
  question,
  onChange,
}: {
  question: ServedQuestion;
  onChange: () => void;
}) {
  const draft = question.draft ?? question.answer ?? null;
  const closed = question.status === "closed_by_praxis";
  return (
    <div className="flex flex-col items-end gap-1">
      {draft === null ? (
        <EmptyState className="max-w-[80%] px-3 py-2 text-[12.5px] text-muted-foreground">
          {closed ? "No longer needed" : "Ask me later"}
        </EmptyState>
      ) : (
        <Panel tone="brand" pad="snug" className="max-w-[80%] text-[13px]">
          {draft.not_said ? (
            <b>{NOT_SAID}</b>
          ) : (
            (draft.choices ?? []).length > 0 && (
              <b>{(draft.choices ?? []).join(", ")}</b>
            )
          )}
          {draft.text && <p>{draft.text}</p>}
        </Panel>
      )}
      {!closed && (
        <Button variant="link" size="dense" onClick={onChange}>
          Change
        </Button>
      )}
    </div>
  );
}

/**
 * The questions so far, each with its reply or skip, and the one on screen
 * with its controls. `panel` is the review's thread: Reply only, no Skip.
 */
export function QuestionThread({
  taskId,
  asked,
  writes,
  panel = false,
}: {
  taskId: string;
  asked: ServedQuestion[];
  writes: Writes;
  panel?: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const current =
    editing ?? asked.find((q) => !settled(q, panel))?.question_id ?? null;
  const shown =
    current === null
      ? asked
      : asked.slice(0, asked.findIndex((q) => q.question_id === current) + 1);
  return (
    <div className="flex flex-col gap-3">
      {shown.map((question) =>
        question.question_id === current ? (
          <Asked key={question.question_id} taskId={taskId} question={question}>
            <ReplyControls
              question={question}
              writes={writes}
              canSkip={!panel}
              onDone={() => {
                setEditing(null);
              }}
            />
          </Asked>
        ) : (
          <div key={question.question_id} className="flex flex-col gap-2">
            <Asked taskId={taskId} question={question} />
            <Replied
              question={question}
              onChange={() => {
                setEditing(question.question_id);
              }}
            />
          </div>
        ),
      )}
    </div>
  );
}

/**
 * Praxis's questions at one commit, as v0.28 draws them: the thread, then the
 * commit's own button, held until each question has a reply or a skip. The
 * button answers the replies, clears the queue and goes on through the commit.
 */
export function QuestionCard({
  taskId,
  store,
  queue,
  topic,
  cta,
  onCleared,
}: {
  taskId: string;
  store: QuestionStoreResponse;
  queue: ServedQueue;
  topic: QuestionTopic;
  cta: string;
  onCleared: () => void;
}) {
  const id = useId();
  const writes = useQuestionWrites(taskId);
  const asked = (queue.question_ids ?? []).flatMap((qid) =>
    store.questions.filter((q) => q.question_id === qid),
  );
  const left = asked.filter((q) => !settled(q, false)).length;
  const done = left === 0;
  const replied = asked
    .filter((q) => Boolean(q.draft) && q.status !== "answered")
    .map((q) => q.question_id);
  return (
    <Panel
      as="section"
      aria-labelledby={id}
      tone="info"
      pad="roomy"
      // The tint is the card's ground, as v0.28 draws it; the words keep the page's ink.
      className="mt-6 text-foreground"
    >
      <CardHeading id={id} asked={asked.length} left={left} />
      <p className="mt-1 text-[12.5px] text-muted-foreground">
        {`Your answers help Praxis understand the ${topic} before it moves on. The count may change as you answer.`}
      </p>
      <div className="mt-4 border-t border-border pt-4">
        <QuestionThread taskId={taskId} asked={asked} writes={writes} />
      </div>
      <div className="mt-5 flex items-center justify-end gap-3">
        {!done && (
          <span className="text-[11px] text-muted-foreground">
            Reply to continue.
          </span>
        )}
        <Button
          held={!done || writes.clear.isPending}
          onClick={() => {
            writes.clear.mutate(
              { queueId: queue.queue_id, replied },
              { onSuccess: onCleared },
            );
          }}
        >
          {cta}
        </Button>
      </div>
      {writes.clear.isError && (
        <Note tone="destructive" role="alert" className="mt-2 text-right">
          {refusalDetail(
            writes.clear.error,
            "Your answers could not be saved. Try again.",
          )}
        </Note>
      )}
    </Panel>
  );
}
